import { isValidPublicId, resolveWirePublicId } from '../lib/publicId'
import { apiRequest } from './client'

export type WorkflowDefinitionStatus = 'draft' | 'published' | 'archived'
export type WorkflowDefinitionOwnerType = 'system' | 'user'

export type WorkflowGraphNode = {
  nodeId: string
  type: string
  name: string
  description: string
  position: Record<string, unknown>
  config: Record<string, unknown>
}

export type WorkflowGraphEdge = {
  edgeId: string
  sourceNodeId: string
  sourcePort: string
  targetNodeId: string
  targetPort: string
  route: string
  condition: string
}

export type WorkflowGraph = {
  nodes: WorkflowGraphNode[]
  edges: WorkflowGraphEdge[]
  inputSchema: Record<string, unknown>
  outputSchema: Record<string, unknown>
}

export type WorkflowDefinition = {
  publicId: string
  ownerType: WorkflowDefinitionOwnerType
  editable: boolean
  sourceDefinitionPublicId: string | null
  name: string
  description: string
  status: WorkflowDefinitionStatus
  visibility: 'private' | 'public'
  tags: string[]
  graph: WorkflowGraph
}

type WorkflowDefinitionWire = WorkflowDefinition & {
  id?: string
  public_id?: string
  sourceDefinitionId?: string | null
  sourceDefinitionPublicId?: string | null
  source_definition_public_id?: string | null
}

function normalizeWorkflowGraph(graph: WorkflowGraph | null | undefined): WorkflowGraph {
  const safe = graph ?? { nodes: [], edges: [], inputSchema: {}, outputSchema: {} }
  return {
    ...safe,
    nodes: (safe.nodes ?? []).map((node) => {
      const legacyId = (node as { id?: string }).id
      return {
        ...node,
        nodeId: String(node.nodeId ?? legacyId ?? '').trim(),
      }
    }),
    edges: (safe.edges ?? []).map((edge) => {
      const legacyId = (edge as { id?: string }).id
      return {
        ...edge,
        edgeId: String(edge.edgeId ?? legacyId ?? '').trim(),
      }
    }),
  }
}

function normalizeWorkflowDefinition(item: WorkflowDefinitionWire): WorkflowDefinition {
  const publicId = resolveWirePublicId(item, 'publicId', 'public_id', 'id')
  const sourceDefinitionPublicId =
    item.sourceDefinitionPublicId ??
    item.source_definition_public_id ??
    item.sourceDefinitionId ??
    null
  return {
    ...item,
    publicId,
    sourceDefinitionPublicId,
    graph: normalizeWorkflowGraph(item.graph),
  }
}

/** 兼容旧响应字段 id → publicId */
export function workflowDefinitionPublicId(
  definition: Pick<WorkflowDefinition, 'publicId'> & { id?: string; public_id?: string },
): string {
  return resolveWirePublicId(definition, 'publicId', 'public_id', 'id')
}

export function isValidWorkflowDefinitionPublicId(value: string | null | undefined): boolean {
  return isValidPublicId(value, { extraRejected: ['new'] })
}

export type WorkflowRunStatus =
  | 'queued'
  | 'running'
  | 'awaiting_input'
  | 'succeeded'
  | 'failed'
  | 'cancelled'

export type WorkflowRunStep = {
  key: string
  label: string
  nodeId: string
  nodeType: string
  status: WorkflowRunStatus
  externalRefType: string
  externalRefId: string
  output: Record<string, unknown>
  errorMessage: string | null
}

export type WorkflowRun = {
  publicId: string
  definitionPublicId: string
  status: WorkflowRunStatus
  currentStepIndex: number
  cancelled: boolean
  input: Record<string, unknown>
  steps: WorkflowRunStep[]
  output: Record<string, unknown>
  errorMessage: string | null
  createdAt: string
  updatedAt: string
}

type WorkflowRunWire = WorkflowRun & {
  id?: string
  public_id?: string
  definitionId?: string
  definitionPublicId?: string
  definition_public_id?: string
}

export function normalizeWorkflowRun(item: WorkflowRunWire): WorkflowRun {
  return {
    ...item,
    publicId: resolveWirePublicId(item, 'publicId', 'public_id', 'id'),
    definitionPublicId: resolveWirePublicId(
      item,
      'definitionPublicId',
      'definition_public_id',
      'definitionId',
    ),
  }
}

export function isValidWorkflowRunPublicId(value: string | null | undefined): boolean {
  return isValidPublicId(value)
}

export type WorkflowStepCatalogItem = {
  type: string
  name: string
  description: string
  category: string
  inputPorts: WorkflowPortDefinition[]
  outputPorts: WorkflowPortDefinition[]
  configSchema: {
    key: string
    label: string
    inputType: 'text' | 'textarea' | 'number' | 'select'
    required: boolean
    placeholder: string
    hint: string
    options: { value: string; label: string }[]
  }[]
  defaultConfig: Record<string, unknown>
  /** 当前环境是否具备执行该节点的能力；false 时节点库置灰（如：没有可用文本模型时的 LLM 节点） */
  available?: boolean
  /** 不可用原因，用于禁用态提示 */
  unavailableReason?: string
}

export type WorkflowPortDefinition = {
  id: string
  label: string
  direction: 'input' | 'output'
  required: boolean
  multiple: boolean
  accepts: string[]
  produces: string
}

export function listWorkflowDefinitions(scope: 'all' | 'system' | 'mine' = 'all') {
  const params = new URLSearchParams({ scope })
  return apiRequest<WorkflowDefinitionWire[]>(`/v1/workflow-definitions?${params.toString()}`, { method: 'GET' }).then(
    (items) => items.map(normalizeWorkflowDefinition),
  )
}

export function getWorkflowDefinition(definitionId: string) {
  return apiRequest<WorkflowDefinitionWire>(`/v1/workflow-definitions/${encodeURIComponent(definitionId)}`, {
    method: 'GET',
  }).then(normalizeWorkflowDefinition)
}

export function listWorkflowStepCatalog() {
  return apiRequest<WorkflowStepCatalogItem[]>('/v1/workflow-step-catalog', { method: 'GET' })
}

export type WorkflowDefinitionUpsertRequest = {
  name: string
  description?: string
  visibility?: 'private' | 'public'
  tags?: string[]
  graph: WorkflowGraph
}

export function createWorkflowDefinition(body: WorkflowDefinitionUpsertRequest) {
  return apiRequest<WorkflowDefinitionWire>('/v1/workflow-definitions', {
    method: 'POST',
    body: JSON.stringify(body),
  }).then(normalizeWorkflowDefinition)
}

export function updateWorkflowDefinition(definitionId: string, body: WorkflowDefinitionUpsertRequest) {
  return apiRequest<WorkflowDefinitionWire>(`/v1/workflow-definitions/${encodeURIComponent(definitionId)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  }).then(normalizeWorkflowDefinition)
}

export function publishWorkflowDefinition(definitionId: string) {
  return apiRequest<WorkflowDefinitionWire>(
    `/v1/workflow-definitions/${encodeURIComponent(definitionId)}/publish`,
    { method: 'POST' },
  ).then(normalizeWorkflowDefinition)
}

export function cloneWorkflowDefinition(definitionId: string) {
  return apiRequest<WorkflowDefinitionWire>(
    `/v1/workflow-definitions/${encodeURIComponent(definitionId)}/clone`,
    { method: 'POST' },
  ).then(normalizeWorkflowDefinition)
}

export function createWorkflowRun(body: {
  definitionId: string
  input?: Record<string, unknown>
  triggerDomain?: string
  triggerRefId?: string
}) {
  return apiRequest<WorkflowRunWire>('/v1/workflow-runs', {
    method: 'POST',
    body: JSON.stringify(body),
  }).then(normalizeWorkflowRun)
}

export function listActiveWorkflowRuns() {
  return apiRequest<WorkflowRunWire[]>('/v1/workflow-runs/active', { method: 'GET' }).then((items) =>
    items.map(normalizeWorkflowRun).filter((run) => isValidWorkflowRunPublicId(run.publicId)),
  )
}

export function getWorkflowRun(runId: string) {
  return apiRequest<WorkflowRunWire>(`/v1/workflow-runs/${encodeURIComponent(runId)}`, {
    method: 'GET',
  }).then(normalizeWorkflowRun)
}

export function cancelWorkflowRun(runId: string) {
  return apiRequest<WorkflowRunWire>(`/v1/workflow-runs/${encodeURIComponent(runId)}/cancel`, {
    method: 'POST',
  }).then(normalizeWorkflowRun)
}

export type WorkflowGraphLintIssueWire = {
  level: 'error' | 'warning'
  code: string
  message: string
  nodeId: string
}

export type WorkflowRuntimeStatus = {
  creationQueueEnabled: boolean
  workflowWorkerAvailable: boolean
  platforms: {
    platform: string
    enabled: boolean
    apiKeyConfigured: boolean
    readyForUse: boolean
  }[]
}

export type WorkflowPreflightResult = {
  runnable: boolean
  issues: WorkflowGraphLintIssueWire[]
  runtime: WorkflowRuntimeStatus
}

export function getWorkflowRuntimeStatus() {
  return apiRequest<WorkflowRuntimeStatus>('/v1/workflow-runtime-status', { method: 'GET' })
}

export function preflightWorkflowDefinition(body: {
  definitionId?: string
  graph?: WorkflowGraph
  input?: Record<string, unknown>
}) {
  return apiRequest<WorkflowPreflightResult>('/v1/workflow-definitions/preflight', {
    method: 'POST',
    body: JSON.stringify(body),
  })
}
